import { useModal } from 'vue-final-modal'
import { i18n } from '@/plugins/i18n'
import AlertModal from '@/components/ui/AlertModal.vue'

// Message lookup kept from the old sweetalert notify plugin: the key itself,
// then `error_<key>` (API error codes), then a generic error naming the key.
function resolveMessage(key) {
  const { t, te } = i18n.global
  if (te(key)) return t(key)
  if (te(`error_${key}`)) return t(`error_${key}`)
  return `${t('error_unknown')} (${key})`
}

// English fallbacks for the default labels, in case translations haven't loaded yet.
const LABEL_FALLBACKS = {
  common_are_you_sure: 'Are you sure?',
  common_confirm: 'Confirm',
  common_cancel: 'Cancel',
  common_yes: 'Yes'
}

// Titles and button labels: an i18n key or plain text.
function resolveLabel(keyOrText) {
  const { t, te } = i18n.global
  if (!keyOrText) return keyOrText
  if (te(keyOrText)) return t(keyOrText)
  return LABEL_FALLBACKS[keyOrText] || keyOrText
}

const openDialogs = new Set()

/**
 * Opens an AlertModal and resolves once it has closed:
 * true when the confirm button was pressed, false otherwise.
 *
 * text: i18n key / API error code, or plain text via `textOverride`
 * opts: { icon, title, textOverride, confirmLabel, cancelLabel, danger, blocking }
 */
function open(text, opts = {}) {
  const {
    icon = 'warning',
    title = '',
    textOverride = null,
    confirmLabel = 'OK',
    cancelLabel = '',
    danger = false,
    blocking = false
  } = opts

  return new Promise((resolve) => {
    let confirmed = false

    const modal = useModal({
      component: AlertModal,
      attrs: {
        icon,
        danger,
        blocking,
        title: resolveLabel(title),
        text: textOverride || resolveMessage(text),
        confirmLabel: resolveLabel(confirmLabel),
        cancelLabel: resolveLabel(cancelLabel),
        onConfirm() {
          confirmed = true
        },
        // resolving after the leave transition lets callers chain the next dialog
        onClosed() {
          openDialogs.delete(modal)
          resolve(confirmed)
        }
      }
    })

    openDialogs.add(modal)
    modal.open()
  })
}

export const dialog = {
  // single OK button
  alert(text, opts = {}) {
    return open(text, opts)
  },

  // OK + Cancel; resolves true only on confirm
  confirm(text, opts = {}) {
    return open(text, {
      title: 'common_are_you_sure',
      confirmLabel: 'common_confirm',
      cancelLabel: 'common_cancel',
      ...opts
    })
  },

  // closes dialogs opened here (not template-bound SheetModals); used on route change
  closeAll() {
    openDialogs.forEach((modal) => modal.close())
  }
}

export default {
  install(app) {
    app.config.globalProperties.$dialog = dialog
    app.provide('dialog', dialog)
  }
}
